import React from "react";
import {
  Typography,
  List,
  ListItem,
  ListItemText,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import ContentPageLayout, { bodyTextSx, sectionTitleSx } from "../../Pages/ContentPageLayout";
import { NAV } from "../../Navbar/navbarTheme";
import {
  privacyIntro,
  privacySections,
  cookieTableRows,
} from "./privacySections";

const listItemSx = {
  padding: 0,
  display: "list-item",
  pl: 2.5,
  mb: 0.5,
};

const bulletListSx = {
  color: NAV.ink,
  mb: 2,
  pl: { xs: 2, sm: 2.5 },
  listStyleType: "disc",
};

const subsectionHeadingSx = {
  color: NAV.navy,
  fontSize: { xs: "1rem", md: "1.0625rem" },
  fontWeight: 600,
  mt: 2,
  mb: 1,
};

const linkSx = {
  color: NAV.navy,
  fontWeight: 600,
  textDecoration: "underline",
};

function PrivacyBulletList({ items }) {
  if (!items?.length) return null;
  return (
    <List dense sx={bulletListSx}>
      {items.map((item) => (
        <ListItem key={item} sx={listItemSx}>
          <ListItemText
            primary={item}
            primaryTypographyProps={{
              sx: { fontSize: "1rem", lineHeight: 1.65 },
            }}
          />
        </ListItem>
      ))}
    </List>
  );
}

function WhoWeAreBlock() {
  return (
    <Box sx={{ mb: 2 }}>
      <Typography sx={{ ...bodyTextSx, mb: 0.5, fontWeight: 600 }}>
        UK TO MARS LTD
      </Typography>
      <Typography sx={{ ...bodyTextSx, mb: 0 }}>
        Company Number: 15764998
        <br />
        Registered Office: 15 Little Balmer, Buckingham Industrial Estate,
        Buckingham, MK18 1TF, United Kingdom
        <br />
        Email:{" "}
        <Box component="a" href="mailto:admin@uktomars.co.uk" sx={linkSx}>
          admin@uktomars.co.uk
        </Box>
        <br />
        Telephone: +44 1280 733473
        <br />
        WhatsApp: +44 7564 627910
      </Typography>
    </Box>
  );
}

function CookieTable() {
  return (
    <TableContainer
      component={Paper}
      variant="outlined"
      sx={{ mb: 2, borderColor: "divider", overflowX: "auto" }}
    >
      <Table size="small" sx={{ minWidth: 520 }}>
        <TableHead>
          <TableRow sx={{ bgcolor: "grey.50" }}>
            <TableCell sx={{ fontWeight: 700, color: NAV.navy }}>Cookie type</TableCell>
            <TableCell sx={{ fontWeight: 700, color: NAV.navy }}>Purpose</TableCell>
            <TableCell sx={{ fontWeight: 700, color: NAV.navy }}>
              Consent required?
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {cookieTableRows.map((row) => (
            <TableRow key={row.type}>
              <TableCell sx={{ verticalAlign: "top", fontWeight: 600 }}>
                {row.type}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top", lineHeight: 1.6 }}>
                {row.purpose}
              </TableCell>
              <TableCell sx={{ verticalAlign: "top" }}>{row.consent}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function CookiesSectionContent() {
  return (
    <>
      <Typography sx={bodyTextSx}>
        Cookies are small text files placed on your device when you visit a
        website. They help websites work, improve performance, remember
        preferences, and provide information to website owners. Similar
        technologies such as pixels, tags, and local storage may also be used,
        and are covered by this section.
      </Typography>
      <Typography sx={bodyTextSx}>
        We use cookies in accordance with the Privacy and Electronic
        Communications Regulations (PECR) and UK GDPR. We may use the following
        categories of cookies:
      </Typography>
      <CookieTable />
      <Typography sx={bodyTextSx}>
        Strictly necessary cookies are always active because the website cannot
        function properly without them. All other categories are only used where
        you have given consent. You can manage or disable cookies through your
        browser settings, which allow you to block or delete cookies, or by
        using browser privacy/incognito modes.
      </Typography>
      <Typography sx={bodyTextSx}>
        Some cookies may be set by third-party services we use, such as
        analytics providers, content delivery networks, or embedded tools. These
        third parties may process limited data in accordance with their own
        privacy and cookie policies.
      </Typography>
      <Typography sx={bodyTextSx}>
        Please note that disabling strictly necessary cookies may prevent parts
        of the website, such as account login or the quote basket, from working
        correctly.
      </Typography>
    </>
  );
}

function ContactBlock() {
  return (
    <Box sx={{ mb: 0 }}>
      <Typography sx={{ ...bodyTextSx, mb: 0.5, fontWeight: 600 }}>
        UK TO MARS LTD
      </Typography>
      <Typography sx={{ ...bodyTextSx, mb: 0 }}>
        15 Little Balmer
        <br />
        Buckingham Industrial Estate
        <br />
        Buckingham
        <br />
        MK18 1TF
        <br />
        United Kingdom
      </Typography>
      <Typography sx={{ ...bodyTextSx, mb: 0, mt: 1.5 }}>
        Email:{" "}
        <Box component="a" href="mailto:admin@uktomars.co.uk" sx={linkSx}>
          admin@uktomars.co.uk
        </Box>
        <br />
        Telephone: +44 1280 733473
        <br />
        WhatsApp: +44 7564 627910
      </Typography>
    </Box>
  );
}

function PrivacySectionBlock({ index, section }) {
  const {
    title,
    paragraphs = [],
    bullets,
    closing = [],
    subsections,
    whoWeAre,
    cookieSection,
    rightsContact,
    contact,
  } = section;

  return (
    <Box component="section" sx={{ mb: 1 }}>
      <Typography component="h2" sx={sectionTitleSx}>
        {index}. {title}
      </Typography>

      {whoWeAre ? <WhoWeAreBlock /> : null}

      {cookieSection ? <CookiesSectionContent /> : null}

      {paragraphs.map((text) => (
        <Typography key={text} sx={bodyTextSx}>
          {text}
        </Typography>
      ))}

      {subsections?.map((sub) => (
        <Box key={sub.heading}>
          <Typography sx={subsectionHeadingSx}>{sub.heading}</Typography>
          <PrivacyBulletList items={sub.bullets} />
        </Box>
      ))}

      <PrivacyBulletList items={bullets} />

      {closing.map((text) => (
        <Typography key={text} sx={bodyTextSx}>
          {text}
        </Typography>
      ))}

      {rightsContact ? (
        <>
          <Typography sx={bodyTextSx}>
            To exercise your rights, contact:{" "}
            <Box component="a" href="mailto:admin@uktomars.co.uk" sx={linkSx}>
              admin@uktomars.co.uk
            </Box>
          </Typography>
          <Typography sx={bodyTextSx}>
            You can also contact the ICO at{" "}
            <Box
              component="a"
              href="https://www.ico.org.uk"
              target="_blank"
              rel="noopener noreferrer"
              sx={linkSx}
            >
              www.ico.org.uk
            </Box>
            .
          </Typography>
        </>
      ) : null}

      {contact ? <ContactBlock /> : null}
    </Box>
  );
}

const PrivacyPolicy = () => (
  <ContentPageLayout title="Privacy Policy" subtitle="Last updated: 15 June 2026">
    {privacyIntro.map((text) => (
      <Typography key={text} sx={bodyTextSx}>
        {text}
      </Typography>
    ))}

    {privacySections.map((section, index) => (
      <PrivacySectionBlock
        key={section.title}
        index={index + 1}
        section={section}
      />
    ))}
  </ContentPageLayout>
);

export default PrivacyPolicy;
